import {
    createCollectionAvailableEvent,
    isPublicationCardItemWithVideo,
    toLinkWithHint,
    useFeature,
} from '@news-mono/web-common'

import React from 'react'
import { BaseCardProps } from '../Card'
import { CardLinkWrapper, StyledHeadline } from './ArticleCard.styled'
import {
    CardBodyWrapper,
    Teaser,
    ReadMore,
    LargeCardMedia,
    CardContainer,
    TopicBylineWrapper,
    AuthorLink,
    Bullet,
    TopicLink,
    Author,
    Classification,
} from '../components'
import { getByline, getClassification, getTopicLink } from '../utils'
import { CommentsCount } from '../components/card-body/byline/CommentsCount'
import { EditorialType } from '../../../templates'
import { TNLiveComponent } from '../../../content'
import { useProduct } from '../../../__product'

export interface ArticleCardProps extends BaseCardProps<'article-card'> {
    isStandalonePage?: boolean
    isStandaloneCard?: boolean
}

export const ArticleCard = ({
    item,
    onEvent,
    isStandalonePage,
    adTargeting,
    disableImageLazyLoad,
}: Omit<ArticleCardProps, 'kind'>) => {
    const classificationEnabled = useFeature('story-classification')

    const { loaded: isLoaded } = item
    const result = isLoaded ? item.result : undefined
    const articleLink = toLinkWithHint(item)
    const byline = getByline(item)
    const topicLink = getTopicLink(item)
    const classification = getClassification(item, classificationEnabled)
    const showNormalClassification = classification !== EditorialType.Live
    const inlineVideoEnabled = useFeature('tn-inline-video-enabled')
    const willPlayVideoInline =
        isLoaded && inlineVideoEnabled
            ? isPublicationCardItemWithVideo(item.result)
            : false

    return (
        <CardContainer>
            {willPlayVideoInline ? (
                <LargeCardMedia
                    item={item}
                    onEvent={onEvent}
                    willPlayVideoInline={willPlayVideoInline}
                    adUnitPath={adTargeting?.adUnitPath}
                    disableImageLazyLoad={disableImageLazyLoad}
                />
            ) : (
                <CardLinkWrapper to={articleLink}>
                    {classification && !showNormalClassification && (
                        <TNLiveComponent
                            overlayOnImage={true}
                            showBackground={true}
                        />
                    )}
                    <LargeCardMedia
                        item={item}
                        onEvent={onEvent}
                        willPlayVideoInline={willPlayVideoInline}
                        adUnitPath={adTargeting?.adUnitPath}
                        disableImageLazyLoad={disableImageLazyLoad}
                    />
                </CardLinkWrapper>
            )}

            {result && (
                <CardBodyWrapper>
                    <TopicBylineWrapper>
                        {classification && showNormalClassification && (
                            <>
                                <Classification type={classification}>
                                    {classification}
                                </Classification>
                                <Bullet />
                            </>
                        )}
                        <TopicLink to={topicLink}>
                            {result.primaryTopic.title}
                        </TopicLink>
                        <Bullet />
                        {!byline.link ? (
                            <Author>{byline.text}</Author>
                        ) : (
                            <AuthorLink to={byline.link}>
                                {byline.text}
                            </AuthorLink>
                        )}
                        {result.allowCommenting && (
                            <>
                                <Bullet />
                                <CommentsCount articleUrl={result._self} />
                            </>
                        )}
                    </TopicBylineWrapper>
                    <CardLinkWrapper to={articleLink}>
                        <StyledHeadline
                            as={isStandalonePage ? 'h2' : undefined}
                        >
                            {result.shortHeadline}
                        </StyledHeadline>
                        <Teaser>{result.teaser}</Teaser>
                        <ReadMore readTime={result.readTimeMinutes} />
                    </CardLinkWrapper>
                </CardBodyWrapper>
            )}
        </CardContainer>
    )
}
